/**
 * IP Intel Report click tracking.
 *
 * Every event is an ordinary trackClick (PostHog `utech_click` + the site's
 * own site_events mirror), named `ipintel_*` so the admin "IP Intel Report"
 * tab can read the whole page's activity with one `action LIKE 'ipintel_%'`.
 * Props stay small — ids and short strings; the store clips a props blob at 4 KB.
 *
 * IPINTEL_EVENTS below is the full catalogue (action → what it means), so the
 * admin Activity tab can label every action the two pages send.
 */

import { useEffect, type RefObject } from 'react';
import { trackClick } from '../../../utils/analytics';

export type OrderPosition = 'header' | 'hero' | 'scope_card' | 'cta_band' | 'nav';
export type BulkPosition = 'strip' | 'faq';
/** Which IP Intel page an event came from (Shell-level events): the main
 *  page, the intake page, or the IIT Bombay × IRCC programme page. */
export type IpIntelPage = 'landing' | 'intake' | 'ircc';

/**
 * Every ipintel_* action the public pages send, with a short human label.
 * Props per action are listed after the label (for whoever reads the raw rows).
 */
export const IPINTEL_EVENTS: Record<string, string> = {
  // ---- page + chrome (both pages) ----
  ipintel_page_view: 'Page viewed',                         // {available, region, as_country?}
  ipintel_section_view: 'Section seen',                     // {section: scope|trusted|samples|how|faq|bulk|cta}
  ipintel_scroll_depth: 'Scrolled',                         // {depth: 25|50|75|100, page}
  ipintel_nav_click: 'Nav link clicked',                    // {item: logo|cosmos|about_us|ip_intel_report, page}
  ipintel_click_nav_link: 'IP Intel link clicked elsewhere', // {where: nav|footer, from} (sent by the site-wide nav/footer)
  ipintel_nav_login: 'Log In / Sign Up clicked',            // {page}
  ipintel_login_success: 'Logged in (sent to Cosmos)',      // {page}
  ipintel_login_close: 'Login closed',                      // {page}
  ipintel_login_open_signup: 'Login → Sign up',             // {page}
  ipintel_password_reset_done: 'Password reset done',      // {page}
  ipintel_signup_success: 'Signed up (sent to Cosmos)',     // {page}
  ipintel_signup_close: 'Sign up closed',                   // {page}
  ipintel_footer_click: 'Footer link clicked',              // {item: about_us|ip_intel_report|universities|comet|rd_symposium|terms|privacy|linkedin|newsletter, page}
  ipintel_click_whatsapp: 'WhatsApp clicked',               // {page}
  ipintel_click_outbound: 'Outbound link clicked',          // {host, where, page}
  // ---- landing: CTAs ----
  ipintel_click_order: 'Order button clicked',              // {position: header|hero|scope_card|cta_band}
  ipintel_click_contact: 'Contact e-mail clicked',          // {position, region?, order_ref?}
  ipintel_click_see_samples: 'See samples clicked',         // {position: hero|cta_band}
  ipintel_click_how_it_works: 'How it works clicked',
  ipintel_config_retry: 'Price retry clicked',              // {position: hero_card|payment_sheet}
  // ---- landing: bulk pricing ----
  ipintel_click_bulk_pricing: 'Bulk pricing clicked',       // {position: strip|faq}
  ipintel_bulk_modal_open: 'Bulk form opened',              // {position, signed_in}
  ipintel_bulk_submit: 'Bulk form sent',                    // {position, message_len, signed_in}
  ipintel_bulk_success: 'Bulk enquiry received',            // {position, ref}
  ipintel_bulk_error: 'Bulk form error',                    // {error, field?, status, stage: client|server}
  ipintel_bulk_close: 'Bulk form closed',                   // {via: close|escape|backdrop|done, stage: form|success, dirty}
  // ---- landing: sample reports ----
  ipintel_sample_toggle: 'Sample ticked/unticked',          // {report, checked, via: cover|checkbox, index}
  ipintel_sample_cover_error: 'Sample cover failed to load', // {report}
  ipintel_click_download: 'Download clicked',               // {count, reports} | {count: 1, retry: true, report}
  ipintel_lead_form_open: 'Sample form opened',             // {count}
  ipintel_lead_form_close: 'Sample form closed',            // {via, count}
  ipintel_lead_submit: 'Sample form sent',                  // {count, reused}
  ipintel_lead_success: 'Samples downloaded',               // {count, reused, reports}
  ipintel_lead_emailed: 'Samples e-mailed',                 // {count, reused, reports, mode: email|both}
  ipintel_lead_send_again: 'Samples "send again" clicked',  // {count} (wrong address)
  ipintel_lead_error: 'Sample form error',                  // {error, count, mode?}
  // ---- landing: payment sheet ----
  ipintel_payment_modal_open: 'Payment sheet opened',       // {position}
  ipintel_payment_details_invalid: 'Payment details invalid', // {position, fields}
  ipintel_payment_details_submit: 'Payment details sent',   // {position, has_gstin, signed_in}
  ipintel_order_created: 'Order created',                   // {order_ref, position, reused}
  ipintel_checkout_opened: 'Razorpay opened',               // {order_ref, amount, currency}
  ipintel_checkout_dismissed: 'Razorpay closed unpaid',     // {order_ref, via?}
  ipintel_checkout_failed: 'Payment failed',                // {order_ref?, code, description, reason?, step?}
  ipintel_payment_verified: 'Payment verified',             // {order_ref}
  ipintel_payment_cancel: 'Payment sheet closed',           // {via: cancel|close|escape|backdrop, position}
  ipintel_payment_new_order: 'Unconfirmed order cleared',   // {position}
  // ---- in-platform only (props also carry surface: platform, entry: tab|techcard) ----
  ipintel_paid_modal_open: 'Paid — confirmation shown',     // {order_ref, auto_intake}
  ipintel_paid_intake_click: 'Paid — intake link opened',   // {order_ref}
  ipintel_paid_modal_close: 'Paid — confirmation closed',   // {order_ref, via}
  // ---- intake page ----
  ipintel_intake_view: 'Intake page opened',                // {status}
  ipintel_intake_retry_load: 'Intake reload clicked',
  ipintel_intake_check_again: 'Intake "check again" clicked', // {order_ref}
  ipintel_intake_edit: 'Intake edit clicked',               // {status}
  ipintel_intake_cancel_edit: 'Intake edit cancelled',      // {status}
  ipintel_intake_source: 'Intake source picked',            // {source}
  ipintel_intake_cosmos_pick: 'Intake listing picked',      // {utech_technology_id}
  ipintel_intake_cosmos_change: 'Intake listing changed',
  ipintel_intake_file_added: 'Intake file added',           // {ext, mb}
  ipintel_intake_file_rejected: 'Intake file rejected',     // {reason: type|size|count, ext}
  ipintel_intake_file_removed: 'Intake file removed',       // {ext, existing}
  ipintel_intake_file_kept: 'Intake file kept',             // {ext}
  ipintel_intake_submit: 'Intake submitted',                // {source, trl, files, order_ref}
  ipintel_intake_success: 'Intake accepted',                // {order_ref, source, clarification}
  ipintel_intake_error: 'Intake error',                     // {error, fields? | status?}
  ipintel_intake_view_submission: 'Intake "view submission" clicked',
  ipintel_intake_back: 'Back to IP Intel Report clicked',   // {from}
  ipintel_click_delivery_download: 'Report download clicked', // {order_ref}
  // ---- IIT Bombay × IRCC programme page (/ip-intel-report/iit-bombay) ----
  ipintel_ircc_page_view: 'IRCC page viewed',                 // {open, remaining}
  ipintel_ircc_click_register: 'IRCC register clicked',       // {position: nav|hero|scope_card}
  ipintel_ircc_tech_count: 'IRCC technology count changed',   // {count, via: inc|dec}
  ipintel_ircc_submit: 'IRCC registration sent',              // {count}
  ipintel_ircc_success: 'IRCC registration received',        // {ref, count}
  ipintel_ircc_error: 'IRCC registration error',             // {field, stage: client|server}
  ipintel_ircc_reset: 'IRCC "register another" clicked',
  ipintel_ircc_click_case_study: 'IRCC case study clicked',
};

/**
 * Props added to every ipintel_* event while set — the in-platform "IP Intel"
 * tab sets {surface: 'platform', entry: 'tab'} for as long as it is mounted,
 * so Admin → IP Intel can tell in-app activity from the public page without
 * every child component (payment sheet, samples, bulk form) passing it down.
 * An event's own props win over these.
 */
let ambient: Record<string, unknown> | null = null;
export const setIpTrackContext = (ctx: Record<string, unknown> | null): void => { ambient = ctx; };

export const ipTrack = (action: string, props?: Record<string, unknown>): void => {
  // Analytics must never break a click, least of all a payment.
  try { trackClick(action, ambient ? { ...ambient, ...(props || {}) } : props); } catch { /* ignore */ }
};

const clip = (v: unknown, n = 200): string | null =>
  v === null || v === undefined || v === '' ? null : String(v).slice(0, n);

export const ipClip = clip;

/** Which IP Intel page the address bar is on. */
export const currentIpIntelPage = (): IpIntelPage => {
  try {
    const p = window.location.pathname;
    if (p.startsWith('/ip-intel-report/intake')) return 'intake';
    if (p.startsWith('/ip-intel-report/iit-bombay') || p.startsWith('/ip-intel-report/ircc')) return 'ircc';
    return 'landing';
  } catch { return 'landing'; }
};

/**
 * ipintel_section_view {section}, once per section per page load, when the
 * section reaches the upper two-thirds of the viewport. Not a visible-ratio
 * threshold: on a phone the scope and samples sections are several screens
 * tall and would never be a third visible. Sections are found by
 * `data-ipintel-section`, so the page only has to tag them.
 */
export function useSectionViews(ready = true): void {
  useEffect(() => {
    if (!ready || typeof IntersectionObserver === 'undefined') return;
    const seen = new Set<string>();
    const els = Array.from(document.querySelectorAll<HTMLElement>('[data-ipintel-section]'));
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        const name = (e.target as HTMLElement).dataset.ipintelSection || '';
        if (!e.isIntersecting || !name || seen.has(name)) return;
        seen.add(name);
        ipTrack('ipintel_section_view', { section: name });
        io.unobserve(e.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -33% 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [ready]);
}

/**
 * ipintel_scroll_depth {depth, page} at 25 / 50 / 75 / 100 %, once each per
 * page load. Depth is where the bottom of the viewport sits in the document
 * (the usual definition), measured on scroll only — landing on the page is
 * not a scroll. 100 allows a couple of pixels for sub-pixel rounding.
 */
export function useScrollDepth(page: IpIntelPage, ready = true, container?: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!ready || typeof window === 'undefined') return;
    // Inside the platform the page scrolls in its own container, not the window.
    const box = container?.current || null;
    const hit = new Set<number>();
    let raf = 0;
    const check = () => {
      raf = 0;
      let total: number;
      let bottom: number;
      if (box) {
        total = box.scrollHeight;
        bottom = box.scrollTop + box.clientHeight;
      } else {
        const doc = document.documentElement;
        total = Math.max(doc.scrollHeight, document.body?.scrollHeight || 0);
        bottom = window.scrollY + window.innerHeight;
      }
      if (!total) return;
      [25, 50, 75, 100].forEach(d => {
        if (hit.has(d)) return;
        const reached = d === 100 ? bottom >= total - 4 : (bottom / total) * 100 >= d;
        if (!reached) return;
        hit.add(d);
        ipTrack('ipintel_scroll_depth', { depth: d, page });
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(check); };
    const target: HTMLElement | Window = box || window;
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [page, ready, container]);
}
